import { Component, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { filter, Observable, catchError, tap, withLatestFrom } from 'rxjs';
import {
  LOGGEDINUSER, UserAgentService, User, AppInitializerService, AppService, AmplitudeEventsService,
  AmplitudeProperties, AmplitudeService, GtmService, NavigationHelper,
} from '@lc/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'lc-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent {
  private appInitializer = inject(AppInitializerService);
  private amplitudeEventsService = inject(AmplitudeEventsService);
  private amplitudeService = inject(AmplitudeService);
  private gtmService = inject(GtmService);

  private readonly GTAG_MAP = new Map([
    ['prod', 'GTM-TNWHNHJ'],
    ['uat', 'GTM-TWJ36QJ'],
  ]);

  constructor() {
    const store = inject<Store<any>>(Store);
    const router = inject(Router);
    const userAgentService = inject(UserAgentService);

    userAgentService.doBrowserCheck();
    this.appInitializer.initializeKeys('dc-designer-app', AppService.productSuiteName);

    const user$: Observable<User> = store.select(LOGGEDINUSER).pipe(
      filter((user) => !!user?.profile),
    );

    NavigationHelper.urlChanged$(router)
      .pipe(
        tap((url) => this.appInitializer.refreshNotifications(url)),
        withLatestFrom(user$, this.amplitudeEventsService.orderContext$),
        tap(([url, user, orderContext]) => {
          const userProperties = this.getUserProperties(user);
          const event = {
            event: 'virtualPageview',
            page_path: url,
            ...userProperties,
          };

          this.gtmService.pushEvent(this.GTAG_MAP, event);
          this.amplitudeService.trackEvent(
            event.event,
            user.oktaId,
            userProperties,
            { page_path: event.page_path, ...orderContext },
          );
        }),
        catchError((err) => { throw new Error(err); }),
        takeUntilDestroyed(),
      ).subscribe();

    this.amplitudeEventsService.events$
      .pipe(
        withLatestFrom(user$),
        tap(([event, user]) => this.amplitudeService.trackEvent(
          event.eventType,
          user.oktaId,
          this.getUserProperties(user),
          event.properties,
        )),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  private getUserProperties(user: User): AmplitudeProperties {
    const auditCreatedDate = user.profile.audit?.createdAt;
    const createdAt = auditCreatedDate
      ? Math.round(new Date(auditCreatedDate).getTime() / 1000)
      : Math.round(new Date().getTime() / 1000);

    return {
      country: 'US',
      userType: 'Designer',
      oktaID: user.oktaId,
      personMDMID: user.personMasterId || '',
      companyMDMID: user.profile.office?.company?.companyMasterId || '',
      companyName: user.profile.office?.company?.companyName || '',
      emailAddress: user.profile.preferredEmail || 'email',
      createdAt,
    };
  }
}
